import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { FiCalendar, FiClock } from 'react-icons/fi';
import TaskHoverTooltip from '../../../../components/common/TaskHoverTooltip';
import TaskCardTooltipContent from './TaskCardTooltipContent';
import {
  dashboardStatusHexColors,
  getDashboardStatusColor,
  getDashboardStatusLabel
} from '../../../../constants/integrationTypes';
import { MdDoNotDisturbAlt } from 'react-icons/md';
import { useSelector } from "react-redux";
import { PriorityCell } from '../../../../shared/molecules/CadenceTable/CadenceTable';

interface UnallocatedTask {
  duration: number;
  item_id: string;
  project_name: string;
  task_type: string;
  task_title: string;
  skill: string;
  start: string;
  end: string;
  project_id: string;
  project_color: string;
  is_primary: boolean;
  completion: number;
  due_date: string;
  status: string;
  integration_status?: string | null;
  priority: string;
  story_points: string;
  story_points_computed?: boolean;
  completed_story_points?: string | number;
  completed_story_points_computed?: boolean;
  original_estimate?: string | number;
  original_estimate_computed?: boolean;
  completed_original_estimate?: string | number;
  completed_original_estimate_computed?: boolean;
  integration_item_type: string;
  stack_rank?: string | number | null;
  integration_team_name?: string;
  integration_project_name?: string;
  closed_outside_sprint?: boolean;
  responsible_id?: string;
}

interface UnallocatedTasksDrawerProps {
  unallocatedTasks: UnallocatedTask[];
  isOpen: boolean;
  onToggle: () => void;
  onTaskClick: (task: UnallocatedTask) => void;
  isSingleTeamSelected?: boolean;
  isSprintFilter?: boolean;
  panelRef?: React.RefObject<HTMLDivElement>; // Ref for scrolling to panel
  onHeightChange?: (height: number) => void; // Callback to notify parent of height changes
}

const UnallocatedTasksDrawer: React.FC<UnallocatedTasksDrawerProps> = ({
  unallocatedTasks = [],
  isOpen,
  onToggle,
  onTaskClick,
  isSingleTeamSelected = false,
  isSprintFilter = false,
  panelRef,
  onHeightChange,
}) => {
  const panelHeight = 140; // Compact height - minimal space usage
  const collapsedHeight = 44; // Height when collapsed
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showShimmer, setShowShimmer] = useState(true); // Control shimmer animation
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const internalPanelRef = useRef<HTMLDivElement>(null);
  const actualPanelRef = panelRef || internalPanelRef;
  const {
    capacityPlanningLoading: isLoading
  } = useSelector((state: any) => state.resources);

  // Notify parent of height changes
  useEffect(() => {
    if (onHeightChange) {
      // Always show at least collapsed height when drawer is visible
      // If loading, show collapsed height (or expanded if already open)
      if (isLoading) {
        const currentHeight = isOpen ? panelHeight : collapsedHeight;
        onHeightChange(currentHeight);
      } else if (unallocatedTasks.length === 0) {
        // Show collapsed header even when no tasks
        onHeightChange(collapsedHeight);
      } else {
        const currentHeight = isOpen ? panelHeight : collapsedHeight;
        onHeightChange(currentHeight);
      }
    }
  }, [isOpen, unallocatedTasks.length, onHeightChange, isLoading]);

  // Note: Auto-expand/collapse is now handled by parent component (Timeline)
  // This effect is kept for backwards compatibility but should not trigger onToggle
  // The parent component manages the isOpen state based on unallocatedTasks

  // Sort tasks by priority (default) or stack_rank if single team selected
  const sortedTasks = useMemo(() => {
    const tasks = [...unallocatedTasks];

    // Helper function to get stack_rank value for sorting
    const getStackRankValue = (stackRank?: string | number | null): number => {
      if (stackRank === null || stackRank === undefined || stackRank === '') return Number.MAX_SAFE_INTEGER;
      if (typeof stackRank === 'number') return stackRank;
      if (typeof stackRank === 'string') {
        const numRank = Number.parseFloat(stackRank);
        if (!Number.isNaN(numRank)) return numRank;
      }
      return Number.MAX_SAFE_INTEGER;
    };

    tasks.sort((a, b) => {
      // If single team selected, sort by stack_rank first
      if (isSingleTeamSelected) {
        const stackRankA = getStackRankValue(a.stack_rank);
        const stackRankB = getStackRankValue(b.stack_rank);
        const stackRankDiff = stackRankA - stackRankB;
        if (stackRankDiff !== 0) return stackRankDiff;
      }

      // Then sort by priority
      const priorityA = Number(a.priority) || 999;
      const priorityB = Number(b.priority) || 999;
      return priorityA - priorityB;
    });

    return tasks;
  }, [unallocatedTasks, isSingleTeamSelected]);

  // Scroll navigation
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Check scroll position to show/hide arrows
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0); // Show left arrow if scrolled right
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth); // Show right arrow if not at end
    }
  };

  // Update arrows on tasks change
  useEffect(() => {
    handleScroll();
  }, [sortedTasks.length]);

  // Restart shimmer when tasks/filters change and stop after 5 seconds
  useEffect(() => {
    setShowShimmer(true);
    const timer = setTimeout(() => {
      setShowShimmer(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [sortedTasks]); // Restart when sortedTasks changes (filters applied)

  // Add scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [scrollContainerRef, isOpen]);

  // Always show the collapsed header bar, even when there are no tasks
  // When open, always use panelHeight (even if no tasks) to show the empty state message
  const currentHeight = isOpen ? panelHeight : collapsedHeight;

  return (
    <div
      ref={actualPanelRef}
      className={`relative w-full bg-white dark:bg-[#1B263B] border-b border-gray-200 dark:border-gray-700
        transition-all duration-300 ease-in-out overflow-hidden
        ${isOpen ? 'shadow-sm' : 'shadow-sm'}
      `}
      id={"unallocated-tasks-panel"}
      style={{ height: `${currentHeight}px` }}
    >
      {/* Collapsed Header - Always Visible (non-clickable) */}
      <div
        className="h-11 flex items-center justify-between px-4 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 border-b-2 border-indigo-300 dark:border-indigo-600/50 transition-all duration-300 relative overflow-hidden"
      >
        {/* Animated background shimmer effect - visible in both light and dark mode, stops after 5 seconds */}
        {/* Only show shimmer when there are tasks */}
        {showShimmer && unallocatedTasks.length > 0 && (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-indigo-400/80 dark:via-indigo-400/50 to-transparent -translate-x-full animate-shimmer"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-300/60 dark:via-purple-300/30 to-transparent -translate-x-full animate-shimmer" style={{ animationDelay: '0.5s' }}></div>
          </>
        )}

        <div className="flex items-center gap-2 relative z-10">
          <div className="relative flex h-2.5 w-2.5 flex-shrink-0">
            <span className="animate-pulse absolute inline-flex h-full w-full rounded-full bg-indigo-400 dark:bg-indigo-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500 dark:bg-indigo-400"></span>
          </div>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Unallocated Work Items
          </span>
          {isLoading ? (
            <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full font-semibold animate-pulse">
              ...
            </span>
          ) : (
            <span className="text-xs bg-indigo-200 dark:bg-indigo-800/60 text-indigo-800 dark:text-indigo-200 px-2 py-0.5 rounded-full font-semibold">
              {sortedTasks.length}
            </span>
          )}
        </div>
        {/*<div className="flex items-center gap-2 right-[72px] relative z-10">*/}
        {/*  {isOpen ? (*/}
        {/*    <HiChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />*/}
        {/*  ) : (*/}
        {/*    <HiChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />*/}
        {/*  )}*/}
        {/*</div>*/}
      </div>

      {/* Expanded Content - Show when open (even if no tasks) */}
      {isOpen && (
        <>
          {/* Content - Horizontal Scroll List with Navigation Arrows */}
          <div
            className="relative overflow-hidden bg-gray-50 dark:bg-[#111928]"
            style={{ height: `${panelHeight - 44}px` }}
          >
            {/* Left Arrow */}
            {showLeftArrow && sortedTasks.length > 0 && (
              <button
                onClick={scrollLeft}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-full p-1.5 shadow-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-all hover:scale-110"
                aria-label="Scroll left"
              >
                <HiChevronDown className="w-4 h-4 rotate-90 text-gray-700 dark:text-gray-300" />
              </button>
            )}

            {/* Right Arrow */}
            {showRightArrow && sortedTasks.length > 0 && (
              <button
                onClick={scrollRight}
                className="absolute right-[72px] top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-full p-1.5 shadow-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-all hover:scale-110"
                aria-label="Scroll right"
              >
                <HiChevronDown className="w-4 h-4 -rotate-90 text-gray-700 dark:text-gray-300" />
              </button>
            )}

            <div
              ref={scrollContainerRef}
              className="h-full px-2 py-2 overflow-x-auto custom-scrollbar scroll-smooth"
              style={{ marginRight: 68 }}
            >
              {isLoading ? (
                <div className="flex gap-2 h-full items-center">
                  {[1, 2, 3, 4].map((index) => (
                    <div
                      key={index}
                      className="flex-shrink-0 h-full bg-white dark:bg-[#1B263B] border border-gray-200 dark:border-[#394256]
                        rounded-lg overflow-hidden min-w-[320px] max-w-[320px] animate-pulse"
                    >
                      <div className="flex items-center gap-2 px-3 py-2 h-full">
                        <div className="flex-1 min-w-0 flex flex-col gap-1">
                          {/* Top row skeleton */}
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-3 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                          </div>
                          {/* Title skeleton */}
                          <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                          {/* Bottom row skeleton */}
                          <div className="flex items-center gap-2">
                            <div className="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="h-2.5 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            <div className="flex items-center gap-1">
                              <div className="w-12 h-1 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                              <div className="h-2.5 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : sortedTasks.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                  <MdDoNotDisturbAlt className="w-8 h-8 mb-2 opacity-30" />
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 text-center">
                    No work items available for allocation
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 text-center mt-1">
                    All work items in this sprint have been assigned to team members
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 h-full items-center">
                  {sortedTasks.map((task, index) => {
                    const status = task?.status?.toLowerCase?.() || '';
                    const statusLabel = getDashboardStatusLabel(status);
                    const statusHexColor = dashboardStatusHexColors[status] || '#9ca3af';
                    const progress = task.completion ?? 0;

                    return (
                      <TaskHoverTooltip
                        key={`${task.item_id}-${index}`}
                        placement="bottom"
                        arrow
                        title={
                          <TaskCardTooltipContent
                            task={task as any}
                            statusLabel={statusLabel}
                            statusColor={statusHexColor}
                            isSprintFilter={isSprintFilter}
                            isSingleTeamSelected={isSingleTeamSelected}
                          />
                        }
                      >
                        <div
                          onClick={() => onTaskClick(task)}
                          className="flex-shrink-0 h-full bg-white dark:bg-[#1B263B] border-t border-r border-b border-gray-200 dark:border-[#394256]
                          hover:bg-gray-50 dark:hover:bg-[#212e47] hover:shadow-md dark:hover:shadow-lg hover:border-indigo-300 dark:hover:border-indigo-600
                          transition-all duration-200 rounded-lg overflow-hidden cursor-pointer group flex items-center gap-2 px-3 py-2
                          min-w-[320px] max-w-[320px]"
                          style={{
                            borderLeftColor: statusHexColor,
                            borderLeftWidth: '4px',
                          }}
                        >
                          {/* Compact horizontal layout */}
                          <div className="flex-1 min-w-0 flex flex-col gap-1">
                            {/* Top: Priority/Stack Rank + Status + Story Points */}
                            <div className="flex items-center gap-2">
                              {isSprintFilter && isSingleTeamSelected && task.stack_rank !== null && task.stack_rank !== undefined && task.stack_rank !== '' && (
                                <span className="text-[9px] px-1 rounded font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 whitespace-nowrap">
                                  SR:{task.stack_rank}
                                </span>
                              )}
                              {task.priority && (
                                <PriorityCell priority={task.priority} className={`inline-flex items-center px-1.5 py-[1px] rounded text-[9px] font-medium text-white flex-shrink-0`}/>
                              )}
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium text-white flex-shrink-0 ${getDashboardStatusColor(status)}`}>
                                {statusLabel}{task.integration_status && task.integration_status.toLowerCase() !== status && ` (${task.integration_status.charAt(0).toUpperCase() + task.integration_status.slice(1).toLowerCase()})`}
                              </span>
                              {task.story_points && task.story_points !== '0' && ['user story', 'bug', 'spike'].includes(task.integration_item_type?.toLowerCase() || '') && (
                                <span className="inline-flex items-center gap-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap">
                                  SP:{task.story_points}
                                  {task.story_points_computed && (
                                    <span className="text-yellow-500 dark:text-yellow-400" title="Story points computed by Cadence Pro">★</span>
                                  )}
                                  {task.completed_story_points !== undefined && task.completed_story_points !== null && (
                                    <>
                                      <span className="text-indigo-400 dark:text-indigo-500">|</span>
                                      <span className="text-green-600 dark:text-green-400">{task.completed_story_points}</span>
                                      {task.completed_story_points_computed && (
                                        <span className="text-yellow-500 dark:text-yellow-400" title="Actual story points computed by Cadence Pro">★</span>
                                      )}
                                      <span className="text-indigo-600 dark:text-indigo-300 font-normal">Completed</span>
                                    </>
                                  )}
                                </span>
                              )}
                              {task.original_estimate && task.original_estimate !== '0' && task.integration_item_type?.toLowerCase() === 'task' && (
                                <span className="inline-flex items-center gap-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap">
                                  OE:{task.original_estimate}
                                  {task.original_estimate_computed && (
                                    <span className="text-yellow-500 dark:text-yellow-400" title="Original estimate computed by Cadence Pro">★</span>
                                  )}
                                  {task.completed_original_estimate !== undefined && task.completed_original_estimate !== null && (
                                    <>
                                      <span className="text-indigo-400 dark:text-indigo-500">|</span>
                                      <span className="text-green-600 dark:text-green-400">{task.completed_original_estimate}</span>
                                      {task.completed_original_estimate_computed && (
                                        <span className="text-yellow-500 dark:text-yellow-400" title="Completed estimate computed by Cadence Pro">★</span>
                                      )}
                                      <span className="text-indigo-600 dark:text-indigo-300 font-normal">Completed</span>
                                    </>
                                  )}
                                </span>
                              )}
                            </div>

                            {/* Task Title - Single line, truncated */}
                            <div className="text-xs font-semibold text-gray-900 dark:text-white leading-tight group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors overflow-hidden text-ellipsis whitespace-nowrap">
                              {task.task_title}
                            </div>

                            {/* Bottom row: Duration + Due Date | Progress on right */}
                            <div className="flex items-center justify-between text-[10px]">
                              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                {/* Duration */}
                                <span className="flex items-center gap-1 font-medium whitespace-nowrap">
                                  <FiClock className="w-3 h-3" />
                                  {task.duration}d
                                </span>
                                {/* Date */}
                                {task.due_date && (
                                  <span className="flex items-center gap-1 font-medium whitespace-nowrap">
                                    <FiCalendar className="w-3 h-3" />
                                    Due {new Date(task.due_date).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-12 bg-gray-200 dark:bg-[#2A3446] rounded-full h-1 overflow-hidden">
                                  <div
                                    className="h-1 rounded-full bg-indigo-500 dark:bg-indigo-400 transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <span className="text-[9px] text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap w-8">
                                  {progress}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TaskHoverTooltip>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UnallocatedTasksDrawer;
